import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Mail } from 'lucide-react';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/horoscope');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    let timer: any;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await login({ email, password });
      
      if (response.success) {
        try {
          const userProfile = await profileService.getProfile();
          if (!userProfile || !userProfile.dob) {
            navigate('/profile?onboarding=true');
            return;
          }
        } catch (e) {
          console.error("Failed to check profile", e);
        }
        navigate('/horoscope');
        return;
      }
      
      // Check if login requires email verification
      if (response.requiresVerification) {
        setVerificationEmail(response.email || email);
        setShowVerification(true);
        return;
      }
    } catch (error: any) {
      // Check if error is about email verification
      if (error.response?.data?.requiresVerification) {
        setVerificationEmail(error.response.data.email || email);
        setShowVerification(true);
        return;
      }
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await authService.resendVerification(verificationEmail);
      alert('Verification email has been resent! Please check your inbox.');
      setResendCooldown(60);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    setLoading(true);
    try {
      const response = await login({ email, password });
      
      if (response.success) {
        try {
          const userProfile = await profileService.getProfile();
          if (!userProfile || !userProfile.dob) {
            navigate('/profile?onboarding=true');
            return;
          }
        } catch (e) {
          console.error("Failed to check profile", e);
        }
        navigate('/horoscope');
        return;
      }
      
      if (response.requiresVerification) {
        alert('Your email is still not verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Continue login error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showVerification) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <Mail size={48} strokeWidth={1.5} style={{ margin: '0 auto 16px', color: 'var(--text-primary)' }} />
            <h1>Email Verification Required</h1>
            <p>Please verify your email address before logging in</p>
          </div>

          <div className="verification-info">
            <p>A verification email has been sent to:</p>
            <p className="verification-email"><strong>{verificationEmail}</strong></p>
            <p>Please check your inbox (and spam folder) for the verification link.</p>
          </div>

          <div className="verification-actions">
            <button 
              type="button" 
              onClick={handleContinue} 
              className="login-button"
              disabled={loading}
              style={{ marginBottom: '8px' }}
            >
              {loading ? 'Checking...' : 'Continue'}
            </button>
            
            <button 
              type="button" 
              onClick={handleResendVerification} 
              className="verify-button"
              disabled={loading || resendCooldown > 0}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email'}
            </button>
            
            <button 
              type="button" 
              onClick={() => {
                setShowVerification(false);
              }} 
              className="back-button"
            >
              Back to Login
            </button>
          </div>

          <div className="login-footer">
            <p>
              Don't have an account?  
              <Link to="/signup" className="link">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to access your astrological insights</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/signup" className="link">
              Sign Up
            </Link>
          </p>
          <p>
            <Link to="/forgot-password" className="link">
              Forgot Password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
